import React from 'react';
import { Typography, Grid, Card, Box } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import TransformIcon from '@mui/icons-material/Transform';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import AssessmentIcon from '@mui/icons-material/Assessment';

const CoreCapabilities = () => {
  return (
    <section id="capabilities" className="whitepaper-section">
      <Typography variant="h2" className="section-title">
        Five Core Capabilities
      </Typography>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6} lg={4}>
          <Card className="capability-card">
            <CloudUploadIcon className="capability-icon" sx={{ color: '#60a5fa' }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#e0e7ef' }}>
              1. Scalable Ingestion
            </Typography>
            <Typography variant="body1" sx={{ color: '#b0bec5', lineHeight: 1.7 }}>
              Process <strong>1M+ events/second</strong> with zero data loss. Kafka's distributed architecture handles extreme bursts while maintaining ordered delivery and exactly-once processing semantics.
            </Typography>
            <Box className="code-block" sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: '0.85rem' }}>
                ✓ Horizontal scaling<br/>
                ✓ Auto-partition balancing<br/>
                ✓ Tiered storage (hot/cold)<br/>
                ✓ Multi-region replication
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card className="capability-card">
            <FilterAltIcon className="capability-icon" sx={{ color: '#10b981' }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#e0e7ef' }}>
              2. Smart Filtering
            </Typography>
            <Typography variant="body1" sx={{ color: '#b0bec5', lineHeight: 1.7 }}>
              Reduce SIEM volumes by <strong>40-70%</strong> through intelligent filtering. Stream processing eliminates noisy events while preserving audit trails in cost-effective storage.
            </Typography>
            <Box className="code-block" sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: '0.85rem' }}>
                ✓ Pattern-based filtering<br/>
                ✓ Dynamic rule updates<br/>
                ✓ Context-aware filtering<br/>
                ✓ Compliance retention
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card className="capability-card">
            <TransformIcon className="capability-icon" sx={{ color: '#f59e0b' }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#e0e7ef' }}>
              3. Real-Time Normalization
            </Typography>
            <Typography variant="body1" sx={{ color: '#b0bec5', lineHeight: 1.7 }}>
              Transform diverse log formats into standardized schemas (ECS, OCSF, CEF) at ingestion. Simplifies detection rules and enables cross-platform correlation.
            </Typography>
            <Box className="code-block" sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: '0.85rem' }}>
                ✓ Schema registry integration<br/>
                ✓ ECS/OCSF mapping<br/>
                ✓ Field extraction/parsing<br/>
                ✓ Data quality validation
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card className="capability-card">
            <EnhancedEncryptionIcon className="capability-icon" sx={{ color: '#8b5cf6' }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#e0e7ef' }}>
              4. Context Enrichment
            </Typography>
            <Typography variant="body1" sx={{ color: '#b0bec5', lineHeight: 1.7 }}>
              Enrich events with threat intelligence, geolocation, asset context, and user identity in <strong>&lt;50ms latency</strong>. Improves detection accuracy and reduces analyst investigation time.
            </Typography>
            <Box className="code-block" sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: '0.85rem' }}>
                ✓ Threat intel feeds (STIX/TAXII)<br/>
                ✓ GeoIP enrichment<br/>
                ✓ Asset/CMDB lookups<br/>
                ✓ User/entity behavior
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card className="capability-card">
            <AssessmentIcon className="capability-icon" sx={{ color: '#ec4899' }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#e0e7ef' }}>
              5. Compliance Storage
            </Typography>
            <Typography variant="body1" sx={{ color: '#b0bec5', lineHeight: 1.7 }}>
              Maintain complete audit trails with <strong>7-year retention</strong> at $10-20/TB/month using tiered storage. Meets SOC 2, PCI-DSS, HIPAA, and GDPR requirements.
            </Typography>
            <Box className="code-block" sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: '0.85rem' }}>
                ✓ Immutable audit logs<br/>
                ✓ Tiered archival (S3/Azure)<br/>
                ✓ Fast replay capability<br/>
                ✓ Encrypted at-rest/in-transit
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </section>
  );
};

export default CoreCapabilities;
